import type { Locale } from '@porotutu/i18n'
import { stampedAt } from '@porotutu/i18n'
import type { UseQueryResult } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { messageOf, statusOf } from '@/api'
import { useT } from '@/session/useSession'
import { Loading } from '../Loading/Loading'
import { Button } from '../ui/button'
import { Empty, EmptyContent, EmptyHeader, EmptyTitle } from '../ui/empty'
import styles from './NotificationBell.module.css'

export type InboxEntry<Kind extends string = string> = {
  id: string
  kind: Kind
  subject_type: string
  subject_id: string
  subject_reference: string
  read_at: string | null
  created_at: string
}

export type InboxPage<Kind extends string = string> = {
  data: InboxEntry<Kind>[]
  meta: { offset: number; limit: number; total: number; unread: number }
}

export function Inbox<Kind extends string>({
  inbox,
  locale,
  entryLink,
}: {
  inbox: UseQueryResult<InboxPage<Kind>>
  locale: Locale
  entryLink: (entry: InboxEntry<Kind>, label: string) => ReactNode
}) {
  const t = useT()

  if (inbox.isPending) return <Loading label={t('pages.loading')} />

  if (inbox.isError) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{messageOf(t, statusOf(inbox.error))}</EmptyTitle>
        </EmptyHeader>
        <EmptyContent>
          <Button variant="outline" size="sm" onClick={() => inbox.refetch()}>
            {t('actions.retry')}
          </Button>
        </EmptyContent>
      </Empty>
    )
  }

  if (inbox.data.data.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('notifications.empty')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <ul className={styles.list}>
      {inbox.data.data.map((entry) => (
        <li key={entry.id} className={entry.read_at === null ? styles.fresh : undefined}>
          {entryLink(
            entry,
            t(`notifications.kind.${entry.kind}`, { reference: entry.subject_reference }),
          )}
          <span className={styles.stamp}>{stampedAt(entry.created_at, locale)}</span>
        </li>
      ))}
    </ul>
  )
}
